import { VStack } from '@chakra-ui/react';
import { FormField } from 'Atoms';
import { useCreateInitialCompany } from 'containers/Navigation';
import {
  EsrsAssessmentDocument_,
  GetSubsidiariesWithAssessmentsDocument_,
  useAddEsrsSubsidaryMutation,
  useShortEsrsAssessmentQuery,
  useUpdateEsrsSubsidaryMutation,
  useUpdateManySubsidiariesMaterialityMutation,
  useUpsertReportingUnitLineageMutation,
  useUserCompaniesQuery,
} from 'models';
import { CreatableSelect, Modal } from 'Molecules';
import { useEffect, useMemo, useState } from 'react';
import { useForm, useFormState, Controller } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { useCurrentCompany, useCurrentCompanyId, useToast } from 'utils/hooks';
import { useTranslation } from 'utils/translation';
import { CompanyFields, useAddSubsidiaryCompanyBU } from './EsrsConfig.hooks';
import { Typography } from 'Tokens';
import { ProjectLeaderSelect } from './ProjectLeaderSelect';
import { TeamMembersFilterOption, useTeamMembers } from 'containers/TeamMembers';
import { FrameworkRefs } from '..';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';
import { useAuth } from 'Providers/auth/AuthProvider.tsx';

export const AddSubsidaryModal = ({
  framework,
  isOpen,
  isEdit,
  subsidiary,
  onInvitationModalOpen,
  onClose,
}: {
  framework: FrameworkRefs;
  isOpen: boolean;
  isEdit?: boolean;
  subsidiary?: CompanyFields['company'];
  onInvitationModalOpen?: () => void;
  onClose: () => void;
}) => {
  const { t } = useTranslation('group');
  const { user } = useAuth();
  const toast = useToast();
  const { members } = useTeamMembers(TeamMembersFilterOption.All);
  const [addSubsidiary] = useAddEsrsSubsidaryMutation({
    refetchQueries: [EsrsAssessmentDocument_, GetSubsidiariesWithAssessmentsDocument_],
  });
  const [updateSubsidiary] = useUpdateEsrsSubsidaryMutation({
    refetchQueries: [EsrsAssessmentDocument_, GetSubsidiariesWithAssessmentsDocument_],
  });
  const [updateSubsidiaries] = useUpdateManySubsidiariesMaterialityMutation();
  const nhost = useNhostClient();

  const [loading, setLoading] = useState(false);
  const { companyId } = useCurrentCompanyId();
  const { company: currentCompany } = useCurrentCompany();
  const { esrsAssessmentId } = useParams();
  const createInitialCompany = useCreateInitialCompany();
  const [upsertReportingUnitLineage] = useUpsertReportingUnitLineageMutation();
  const createCompanyLevelBU = useAddSubsidiaryCompanyBU();
  const [selectedSubsidiary, setSelectedSubsidiary] = useState<CompanyFields['company']>();

  const { data: parentAssessmentData } = useShortEsrsAssessmentQuery({
    variables: { esrsAssessmentId },
    skip: !esrsAssessmentId,
  });

  const { data } = useUserCompaniesQuery({
    variables: {
      id: user?.id,
    },
    skip: !user?.id,
  });

  const { control, handleSubmit, reset, setValue } = useForm<CompanyFields>({
    mode: 'all',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
  });
  const firstTeamMemberId = useMemo(() => members?.[0]?.id, [members]);

  useEffect(() => {
    if (subsidiary) setValue('company', subsidiary);
  }, [subsidiary]);

  const selectedCompanyAssessmentProjectLeader = useMemo(() => {
    const esrsAssessment = data?.data?.companies.find(
      (c) => c.company.id === selectedSubsidiary?.id
    )?.company.esrsAssessments;
    const selectedYearAssessment = esrsAssessment?.find(
      (a) => a.reportingYear === parentAssessmentData?.esrsAssessment?.reportingYear
    );

    return isEdit
      ? members.find((member) => member.id === subsidiary?.projectLeaderId)
      : selectedYearAssessment?.projectLeader;
  }, [selectedSubsidiary, data, isEdit]);

  const userCompanies = useMemo(() => {
    return data?.data?.companies.filter(
      (c) => c.company.id !== companyId && c.company.isPortfolioOwner !== true
    );
  }, [data]);

  const materialStandardIds = useMemo(
    () => parentAssessmentData?.esrsAssessment?.materialStandards.map((ms) => ms.id) ?? [],
    [parentAssessmentData]
  );

  const { errors } = useFormState({ control });

  const handleAddSubsidiary = async (subsidiaryData: CompanyFields) => {
    const { company } = subsidiaryData;
    setLoading(true);
    if (!company.id && !isEdit) {
      try {
        const createCompId = await createInitialCompany({
          userId: user?.id ?? '',
          name: company?.name,
          isPortfolioOwner: false,
          isGroupOwner: false,
          currency: currentCompany?.currency ?? 'NOK',
          createFirstBusinessUnit: true,
          hasTaxonomyAccess: currentCompany?.hasTaxonomyAccess ?? true,
          hasESRSAccess: currentCompany?.hasESRSAccess ?? false,
          hasVSMEAccess: currentCompany?.hasVSMEAccess ?? false,
          hasIFRSAccess: currentCompany?.hasIFRSAccess ?? false,
          hasTCFDAccess: currentCompany?.hasTCFDAccess ?? false,
          hasESRSOilAndGasAccess: currentCompany?.hasESRSOilAndGasAccess ?? false,
          hasEntitySpecificAccess: currentCompany?.hasEntitySpecificAccess ?? false,
          hasSASBAccess: currentCompany?.hasSASBAccess ?? false,
        });
        const res = await addSubsidiary({
          variables: {
            esrsAssessment: {
              companyId: createCompId ?? '',
              reportingYear:
                parentAssessmentData?.esrsAssessment?.reportingYear ?? new Date().getUTCFullYear(),
              parentAssessmentId: esrsAssessmentId,
              projectLeaderId: company?.projectLeaderId ?? firstTeamMemberId,
              frameworkRef: framework,
            },
          },
          refetchQueries: [EsrsAssessmentDocument_, GetSubsidiariesWithAssessmentsDocument_],
          awaitRefetchQueries: true,
        });

        await nhost.refreshSession(0);

        await upsertReportingUnitLineage({
          variables: {
            reportingUnitLineage: {
              companyId: createCompId,
              isCompanyLevel: true,
              reportingUnits: {
                data: [
                  {
                    name: 'Company level',
                    assessmentId: res.data?.insert_EsrsAssessment_one?.id,
                    isCompanyLevel: true,
                  },
                ],
              },
            },
          },
          refetchQueries: [EsrsAssessmentDocument_],
        }).then(() => setLoading(false));
        updateSubsidiaries({ variables: { ids: materialStandardIds } });
        onClose();
        toast({
          text: t('toast.added'),
        });
        reset({
          company: undefined,
        });
      } catch (e) {
        onClose();
        setLoading(false);
        reset({
          company: undefined,
        });
        if (String(e).includes('Subsidiary_companyId_groupAssessmentId_key')) {
          toast({
            variant: 'danger',
            text: company.name + t('toast.exists'),
          });
        } else {
          toast({
            variant: 'danger',
            text: t('toast.failed'),
          });
        }
      }
    } else {
      const selectedCompany = userCompanies?.find((c) => c.company.id === company.id)?.company
        .esrsAssessments;
      const selectedYearAssessment = selectedCompany?.find(
        (a) => a.reportingYear === parentAssessmentData?.esrsAssessment?.reportingYear
      );
      if (selectedYearAssessment?.reportingYear) {
        const res = await updateSubsidiary({
          variables: {
            companyId: company.id ?? '',
            reportingYear:
              parentAssessmentData?.esrsAssessment?.reportingYear ?? new Date().getUTCFullYear(),
            parentAssessmentId: esrsAssessmentId,
            projectLeaderId:
              company.projectLeaderId ??
              selectedCompanyAssessmentProjectLeader?.id ??
              firstTeamMemberId,
            frameworkRef: framework,
          },
          refetchQueries: [EsrsAssessmentDocument_],
        });
        updateSubsidiaries({ variables: { ids: materialStandardIds } });
        createCompanyLevelBU(
          company,
          userCompanies,
          parentAssessmentData?.esrsAssessment?.reportingYear,
          res.data?.update_EsrsAssessment?.returning?.[0].id
        );
      } else {
        const res = await addSubsidiary({
          variables: {
            esrsAssessment: {
              companyId: company.id ?? '',
              reportingYear:
                parentAssessmentData?.esrsAssessment?.reportingYear ?? new Date().getUTCFullYear(),
              parentAssessmentId: esrsAssessmentId,
              projectLeaderId: subsidiaryData.company?.projectLeaderId ?? firstTeamMemberId,
              frameworkRef: framework,
            },
          },
          refetchQueries: [EsrsAssessmentDocument_, GetSubsidiariesWithAssessmentsDocument_],
          awaitRefetchQueries: true,
        });
        updateSubsidiaries({ variables: { ids: materialStandardIds } });
        createCompanyLevelBU(
          company,
          userCompanies,
          parentAssessmentData?.esrsAssessment?.reportingYear,
          res.data?.insert_EsrsAssessment_one?.id
        );
      }
      onClose();
      setLoading(false);
      toast({
        text: t('toast.added'),
      });
      reset({
        company: undefined,
      });
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('companies.addSubsidiary')}
      confirmButtonProps={{
        type: 'submit',
        form: 'subsidiary-form',
      }}
      loading={loading}
    >
      <form onSubmit={handleSubmit(handleAddSubsidiary)} id="subsidiary-form">
        <VStack alignItems="stretch" spacing="16px">
          <Controller
            name="company"
            control={control}
            rules={{ required: t('companies.required.name') }}
            render={({ field: { onChange, value } }) => {
              return (
                <>
                  <FormField
                    label={t('companies.name')}
                    id="name"
                    isRequired
                    isInvalid={!!errors.company}
                    error={t('companies.required.name')}
                  >
                    <CreatableSelect
                      value={value}
                      defaultValue={subsidiary}
                      isClearable={true}
                      isDisabled={isEdit}
                      options={userCompanies?.map((c) => {
                        return c.company;
                      })}
                      createOptionPosition="first"
                      placeholder={
                        <Typography color={'text.hint'} variant="body">
                          {t('companies.fields.company.placeholder')}
                        </Typography>
                      }
                      getOptionValue={(company) => company.id ?? ''}
                      getOptionLabel={(company) => company.name}
                      getNewOptionData={(_input, label) => ({
                        id: undefined,
                        name: label as string,
                      })}
                      onChange={(company) => {
                        onChange(company ? { ...company } : undefined);
                        setSelectedSubsidiary(company ?? undefined);
                      }}
                      formatCreateLabel={(input) => t('companies.createNew', { name: input })}
                    />
                  </FormField>
                  <FormField
                    label={"Subsidiary's project leader"}
                    id="projectLeader"
                    isInvalid={!!errors.company}
                    error={'Please add a project leader to your subsidiary'}
                  >
                    <ProjectLeaderSelect
                      defaultProjectLeader={selectedCompanyAssessmentProjectLeader ?? undefined}
                      members={members}
                      onInvitationModalOpen={onInvitationModalOpen}
                      closeModal={onClose}
                      onChange={(member) => {
                        setValue('company.projectLeaderId', member.id);
                      }}
                    />
                  </FormField>
                </>
              );
            }}
          />
        </VStack>
      </form>
    </Modal>
  );
};
